import type { Evaluation } from '@compass/client/types/evaluation';
import { CallDirection } from '@compass/client/types/evaluation';

import { buildOrgRoutePath } from '@/utils/routes';
import { parseMultiValueParam, serializeMultiValueParam } from '@/utils/urlParams';
import { isValidObjectId } from '@/utils/validation';

export type QcQuickFilter = 'all' | 'needs_review' | 'escalations' | 'compliance_failure';
export type QcScoreBand = 'all' | 'exemplary' | 'meets' | 'needs_coaching';
export interface QcListFilterState {
  callIdFilter?: string;
  evaluationIdFilter?: string;
  selectedProgramIds?: string[];
  selectedAgentIds?: string[];
  selectedCallDirections?: string[];
  scoreBandFilter?: QcScoreBand;
  activeTab?: QcQuickFilter;
  effectiveDateFrom: string;
  effectiveDateTo: string;
  defaultDateFrom: string;
  defaultDateTo: string;
}
export interface QcDateRangeParams {
  from: string;
  to: string;
}
export interface QcCachedDateRangeState {
  dateFrom?: string;
  dateTo?: string;
}
export type CallIdSearchInput =
  | { kind: 'none' }
  | { kind: 'valid'; values: string[] }
  | { kind: 'invalid' };

// Score band thresholds (as decimal fractions, matching proto/API format).
// Three bands: Exemplary (≥98%), Meets Expectations (95–97.9%), and Needs
// Coaching (everything below 95% — the catch-all bottom band, no lower bound).
export const SCORE_BAND_EXEMPLARY_MIN = 0.98; // 98%
export const SCORE_BAND_MEETS_MIN = 0.95; // 95%

export const SCORE_BAND_OPTIONS: Array<{ value: QcScoreBand; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'exemplary', label: 'Exemplary (≥98%)' },
  { value: 'meets', label: 'Meets Expectations (95–97.9%)' },
  { value: 'needs_coaching', label: 'Unsatisfactory (<95%)' },
];

export const resolveQcOrganizationId = (
  routeOrganizationId?: string | null,
  selectedOrganizationId?: string | null,
): string | null => routeOrganizationId || selectedOrganizationId || null;

export const buildQcListPath = (
  routeOrganizationId?: string | null,
  selectedOrganizationId?: string | null,
): string => {
  const organizationId = resolveQcOrganizationId(routeOrganizationId, selectedOrganizationId);
  return organizationId ? buildOrgRoutePath('/qc', organizationId) : '/';
};

export const parseCallIdSearchInput = (value?: string): CallIdSearchInput => {
  if (!value?.trim()) {
    return { kind: 'none' };
  }

  const validCallIds = value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
    .filter(isValidObjectId);
  if (validCallIds.length === 0) {
    return { kind: 'invalid' };
  }

  return { kind: 'valid', values: validCallIds };
};

export const hasActiveQcListFilters = ({
  callIdFilter,
  evaluationIdFilter,
  selectedProgramIds = [],
  selectedAgentIds = [],
  selectedCallDirections = [],
  scoreBandFilter = 'all',
  activeTab = 'all',
  effectiveDateFrom,
  effectiveDateTo,
  defaultDateFrom,
  defaultDateTo,
}: QcListFilterState): boolean => {
  const hasActiveSearch = Boolean(callIdFilter?.trim() || evaluationIdFilter?.trim());
  const hasActivePrograms = selectedProgramIds.length > 0;
  const hasActiveAgents = selectedAgentIds.length > 0;
  const hasActiveCallDirections = selectedCallDirections.length > 0;
  const hasActiveScoreBand = scoreBandFilter !== 'all';
  const hasActiveTab = activeTab !== 'all';
  const hasCustomDateRange =
    effectiveDateFrom !== defaultDateFrom || effectiveDateTo !== defaultDateTo;

  return [
    hasActiveSearch,
    hasActivePrograms,
    hasActiveAgents,
    hasActiveCallDirections,
    hasActiveScoreBand,
    hasActiveTab,
    hasCustomDateRange,
  ].some(Boolean);
};

export const resolveCachedQcDateRange = (
  cached: QcCachedDateRangeState | null,
  defaultDateRangeParams: QcDateRangeParams,
): QcDateRangeParams => ({
  from: cached?.dateFrom ?? defaultDateRangeParams.from,
  to: cached?.dateTo ?? defaultDateRangeParams.to,
});

const readMetadataValue = (
  metadata: Record<string, unknown> | undefined,
  keys: string[],
): string | null => {
  if (!metadata) {
    return null;
  }

  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  return null;
};

export const getMetadataValue = (evaluation: Evaluation, keys: string[]): string | null => {
  const topLevelValue = readMetadataValue(
    evaluation.metadata as Record<string, unknown> | undefined,
    keys,
  );
  if (topLevelValue) {
    return topLevelValue;
  }

  for (const finding of evaluation.findings || []) {
    const value = readMetadataValue(finding.metadata as Record<string, unknown> | undefined, keys);
    if (value) {
      return value;
    }
  }

  return null;
};

export const formatAggregateScore = (score?: number): string => {
  if (score === undefined || score === null) {
    return 'N/A';
  }
  return `${(score * 100).toFixed(1)}%`;
};

// Call-direction filter options shared by the QC list and analytics. Values are
// stringified CallDirection enum numbers, the wire format of `call_directions`.
export const CALL_DIRECTION_FILTER_OPTIONS = [
  { label: 'Inbound', value: String(CallDirection.CALL_DIRECTION_INBOUND) },
  { label: 'Outbound', value: String(CallDirection.CALL_DIRECTION_OUTBOUND) },
];
const VALID_CALL_DIRECTION_FILTER_VALUES = new Set(
  CALL_DIRECTION_FILTER_OPTIONS.map(option => option.value),
);

/** Parses the `call_directions` URL param, dropping unknown values. */
export const parseCallDirectionsParam = (value: string | null): string[] =>
  parseMultiValueParam(value).filter(v => VALID_CALL_DIRECTION_FILTER_VALUES.has(v));

export const getEvaluationDirectionLabel = (
  direction?: CallDirection | null,
): 'Inbound' | 'Outbound' | 'Internal' | null => {
  switch (direction) {
    case CallDirection.CALL_DIRECTION_INBOUND:
      return 'Inbound';
    case CallDirection.CALL_DIRECTION_OUTBOUND:
      return 'Outbound';
    case CallDirection.CALL_DIRECTION_INTERNAL:
      return 'Internal';
    default:
      return null;
  }
};

export const getEvaluationDirection = (
  evaluation: Evaluation,
): 'Inbound' | 'Outbound' | 'Internal' | null => {
  const enumLabel = getEvaluationDirectionLabel(evaluation.call_direction);
  if (enumLabel) {
    return enumLabel;
  }

  const rawDirection = getMetadataValue(evaluation, ['direction', 'call_direction']);
  if (!rawDirection) {
    return null;
  }

  const normalized = rawDirection.toLowerCase();
  if (normalized.includes('inbound') || normalized === 'in') {
    return 'Inbound';
  }
  if (normalized.includes('outbound') || normalized === 'out') {
    return 'Outbound';
  }
  if (normalized.includes('internal')) {
    return 'Internal';
  }

  return null;
};

// URL param prefix for QC metadata filters, in dot-notation (e.g.
// `metadata.call-reason`). Single source of truth for the contract between the
// producer (analytics breakdown drill-down link) and the consumer (QcContainer's
// param parser + the ListEvaluations request builders); matches the backend's
// HTTP dot-notation `metadata.key=value`.
export const QC_METADATA_PARAM_PREFIX = 'metadata.';

// The exact QC URL filter params (excludes `page`, which is pagination position,
// not a filter). Single source of truth shared by QcContainer's `hasUrlFilters`
// check and the saved-views whitelist, so the two never drift.
export const QC_FILTER_PARAM_KEYS = new Set([
  'tab',
  'search_type',
  'call_id',
  'evaluation_id',
  'program_ids',
  'agent_ids',
  'call_directions',
  'score_band',
  'only_ineligible',
  'only_reviewed',
  'date_from',
  'date_to',
  'sort_by_call_start_time',
  'sort_by_aggregate_score',
  'sort_by_call_duration',
  'sort_by_section_score',
]);

// Analytics filters carried into a QC drill-down so the list matches the number
// that was clicked. Dates are YYYY-MM-DD (analytics start/end = QC date_from/to).
export interface QcDrilldownScope {
  dateFrom?: string;
  dateTo?: string;
  programIds?: string[];
  agentIds?: string[];
  /** CallDirection enum numbers, as in the `call_directions` URL param (AQ-841). */
  callDirections?: string[];
  /** Active advanced filters (key -> values). The clicked key wins over its entry here. */
  metadata?: Record<string, string[]>;
}

// Builds the QC-list drill-down path for a metadata key/value:
//   /org-<id>/qc?metadata.<key>=<value>[&date_from=..&date_to=..&program_ids=..&agent_ids=..&call_directions=..&metadata.<other>=..]
// (key and value URL-encoded). Without a date range QC falls back to its last-7-days default.
export const buildQcMetadataFilterPath = (
  organizationId: string,
  key: string,
  value: string,
  scope: QcDrilldownScope = {},
): string => {
  const otherMetadata = Object.entries(scope.metadata ?? {}).filter(([k]) => k !== key);
  const scopeParams: [string, string | undefined][] = [
    ['date_from', scope.dateFrom],
    ['date_to', scope.dateTo],
    ['program_ids', serializeMultiValueParam(scope.programIds ?? [])],
    ['agent_ids', serializeMultiValueParam(scope.agentIds ?? [])],
    ['call_directions', serializeMultiValueParam(scope.callDirections)],
    ...otherMetadata.map(([k, values]): [string, string] => [
      `${QC_METADATA_PARAM_PREFIX}${k}`,
      values.join(','),
    ]),
  ];
  const params = new URLSearchParams();
  for (const [name, paramValue] of scopeParams) {
    if (paramValue) params.set(name, paramValue);
  }

  const scopeQs = params.toString();
  const qs = `${QC_METADATA_PARAM_PREFIX}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  return `${buildOrgRoutePath('/qc', organizationId)}?${qs}${scopeQs ? `&${scopeQs}` : ''}`;
};

// AQ-653: ineligible-reason handling for the QC detail "Not scorable" card.
//
// The eligibility/QC agent (ethos repo, service/qc.go MarkIneligible) writes a
// free-text reason onto the evaluation as reasons.metadata["ineligibility_reason"].
// The stored strings are technical, not display-ready, so we map them to a clean
// title here. Source of truth for the fixed set: ethos
// evals/eligibility/pqc_eligibility_check.py ELIGIBILITY_KEYS plus the deterministic
// duration/transfer checks in service/qc.go.
const INELIGIBILITY_REASON_METADATA_KEY = 'ineligibility_reason';

// Returns the raw stored ineligibility reason, or null when absent.
export const getIneligibilityReason = (evaluation?: Evaluation): string | null => {
  const raw = evaluation?.reasons?.metadata?.[INELIGIBILITY_REASON_METADATA_KEY];
  return raw && raw.trim() ? raw.trim() : null;
};

// Two maps, two keyspaces, deliberately linked by the spread below so the display
// wording stays identical across both consumers:
//   1. ELIGIBILITY_KEY_TITLES — keyed by the 8 raw LLM eligibility keys (the
//      "<key>: <detail>" prefix). Used by formatIneligibilityReasonTitle, which
//      parses the RAW free-text reason on the detail page. The deterministic
//      duration/transfer reasons arrive as English phrases in raw text, not as
//      keys, so they are matched by string in that function — NOT listed here.
//   2. INELIGIBLE_REASON_BUCKET_TITLES — keyed by the canonical snake_case buckets
//      the analytics backend already normalized (normalizeIneligibilityReason).
//      Server-side those deterministic phrases are folded into bucket keys, so this
//      map adds them (short_duration[_60s], transfer_*) plus the (other)/(none) catch-alls.
//      Used by formatIneligibleReasonBucketTitle for the analytics donut.
// The donut never sees raw strings and the raw parser never sees bucket keys — the
// spread is the only thing keeping their shared labels in sync.

// Human-readable titles for the fixed LLM eligibility keys (the "<key>: <detail>"
// prefix produced by selectEligibilityIneligibility in ethos service/qc.go).
const ELIGIBILITY_KEY_TITLES: Record<string, string> = {
  voicemail: 'Voicemail',
  wrong_number: 'Wrong Number',
  insufficient_exchange: 'Insufficient Exchange',
  language_barrier: 'Language Barrier',
  ivr_automated_only: 'IVR / Automated Only',
  manufacturer_aepqc_reporting: 'Manufacturer AE/PQC Reporting',
  insurance_benefit_check: 'Insurance Benefit Check',
  internal_call: 'Internal Call',
  assets_not_found: 'Assets Not Found',
  no_speech: 'No Speech Detected',
};

// Display titles keyed by the CANONICAL buckets the analytics backend emits
// (service/analytics.go normalizeIneligibilityReason), as opposed to the raw
// reason strings formatIneligibilityReasonTitle handles. Reuses the same
// ELIGIBILITY_KEY_TITLES wording so the analytics donut and the QC detail
// "Not scorable" card agree.
const INELIGIBLE_REASON_BUCKET_TITLES: Record<string, string> = {
  ...ELIGIBILITY_KEY_TITLES,
  short_duration: 'Duration < 30 Seconds',
  short_duration_60s: 'Duration ≤ 60 Seconds',
  transfer_call: 'Transferred Call',
  transfer_event: 'Transferred Call',
  rph_transfer_event: 'Transferred to Pharmacist',
  '(other)': 'Other',
  '(none)': 'Unspecified',
};

// Maps a canonical ineligible-reason bucket key (from the analytics breakdown)
// to a display title. Unknown keys fall back to "Not Scorable".
export const formatIneligibleReasonBucketTitle = (bucketKey: string): string =>
  INELIGIBLE_REASON_BUCKET_TITLES[bucketKey] ?? 'Not Scorable';

// Maps a raw ineligibility_reason string to a display title. Falls back to a
// generic "Not Scorable" so an unrecognized/empty reason still renders cleanly.
export const formatIneligibilityReasonTitle = (rawReason?: string | null): string => {
  const reason = rawReason?.trim();
  if (!reason) {
    return 'Not Scorable';
  }

  const lower = reason.toLowerCase();

  // LLM checks are prefixed "<key>: <detail>" (or a bare key). Match these FIRST
  // and by the key alone — the free-text detail can contain words like "transfer"
  // (e.g. internal_call's "transfer to RPh") that would otherwise trip the
  // deterministic transfer branch below.
  const key = lower.split(':', 1)[0].trim();
  if (ELIGIBILITY_KEY_TITLES[key]) {
    return ELIGIBILITY_KEY_TITLES[key];
  }

  // Deterministic duration check: "call duration <d> is less than minimum 30s" (1m0s before AQ-763).
  if (lower.startsWith('call duration') && lower.includes('less than minimum')) {
    return lower.includes('minimum 1m0s') ? 'Duration ≤ 60 Seconds' : 'Duration < 30 Seconds';
  }

  // Deterministic transfer checks (fixed strings from ethos transferIneligibilityReason):
  // "call is a transfer call", "call contains transfer event", "call contains rph_transfer event".
  if (lower.includes('rph_transfer')) {
    return 'Transferred to Pharmacist';
  }
  if (lower.startsWith('call is a transfer') || lower.includes('transfer event')) {
    return 'Transferred Call';
  }

  return 'Not Scorable';
};
